import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import platformAPI from "../../../services/platformApi";
import {
  ActionFeed,
  ChartTooltipBox,
  formatRub,
  MetricCard,
  MiniFunnel,
  METRIC_TIPS,
} from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const CHART_TABS = [
  { id: "revenue", label: "Выручка" },
  { id: "signups", label: "Клубы" },
  { id: "sessions", label: "Тренировки" },
];

function formatWeekShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

function formatDayShort(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

function mergeSeriesWithForecast(actual = [], projected = []) {
  const hist = actual.map((p) => ({
    name: formatWeekShort(p.week),
    week: p.week,
    actual: p.value,
    forecast: null,
  }));
  if (!hist.length) {
    return projected.map((p) => ({
      name: formatWeekShort(p.week),
      week: p.week,
      actual: null,
      forecast: p.value,
    }));
  }
  const bridge = {
    ...hist[hist.length - 1],
    forecast: hist[hist.length - 1].actual,
  };
  const future = projected.map((p) => ({
    name: formatWeekShort(p.week),
    week: p.week,
    actual: null,
    forecast: p.value,
  }));
  return [...hist.slice(0, -1), bridge, ...future];
}

function pctLabel(v) {
  if (v == null) return "—";
  return `${v}%`;
}

export default function PlatformDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [payStats, setPayStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [chartTab, setChartTab] = useState("revenue");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      platformAPI.getAnalytics(),
      platformAPI.getPaymentsStats().catch(() => null),
      platformAPI.listPayments({ limit: 6, status: "confirmed" }).catch(() => null),
    ])
      .then(([a, p, payments]) => {
        if (cancelled) return;
        setAnalytics(a.analytics || null);
        setPayStats(p?.stats || null);
        setRecentPayments(payments?.payments || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить сводку");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const overview = analytics?.overview || {};
  const growth = analytics?.growth || {};
  const activity = analytics?.activity || {};
  const subscriptions = analytics?.subscriptions || {};
  const forecast = analytics?.forecast || {};
  const funnel = analytics?.funnel || null;
  const atRisk = analytics?.at_risk || {};

  const signupSeries = useMemo(
    () =>
      mergeSeriesWithForecast(
        growth.signups_weekly || [],
        forecast.tenants?.projected || [],
      ),
    [growth.signups_weekly, forecast.tenants?.projected],
  );

  const sessionSeries = useMemo(
    () =>
      mergeSeriesWithForecast(
        activity.sessions_weekly || [],
        forecast.sessions?.projected || [],
      ),
    [activity.sessions_weekly, forecast.sessions?.projected],
  );

  const revenueSeries = useMemo(
    () =>
      (payStats?.revenue_daily || [])
        .filter((_, i, arr) => i >= arr.length - 30)
        .map((p) => ({
          name: formatDayShort(p.day),
          amount: p.amount,
        })),
    [payStats?.revenue_daily],
  );

  const actionItems = useMemo(() => {
    const items = [];
    const expiring = atRisk.expiring_soon || 0;
    const inactive = atRisk.inactive_trials || 0;
    const risk = funnel?.at_risk_count || 0;
    const pending = payStats?.payments_pending || 0;

    if (expiring) {
      items.push({
        id: "expiring",
        title: "Подписка истекает",
        meta: "Нужен контакт в ближайшие 14 дней",
        count: expiring,
        to: "/admin/tenants?filter=expiring",
        tone: "warn",
      });
    }
    if (inactive) {
      items.push({
        id: "inactive",
        title: "Тихие trial",
        meta: "Нет занятий больше 14 дней",
        count: inactive,
        to: "/admin/tenants?filter=inactive",
        tone: "warn",
      });
    }
    if (risk) {
      items.push({
        id: "funnel-risk",
        title: "В риске по воронке",
        meta: "Откройте доску и разберите карточки",
        count: risk,
        to: "/admin/funnel",
        tone: "warn",
      });
    }
    if (pending) {
      items.push({
        id: "pending",
        title: "Оплаты ждут подтверждения",
        meta: "Сессии в статусе pending",
        count: pending,
        to: "/admin/payments",
      });
    }
    (analytics?.insights || []).slice(0, 3).forEach((ins, i) => {
      items.push({
        id: `insight-${i}`,
        title: ins.text,
        tone: ins.type === "warning" ? "warn" : ins.type === "positive" ? "ok" : "",
      });
    });
    return items.slice(0, 7);
  }, [analytics?.insights, atRisk, funnel?.at_risk_count, payStats?.payments_pending]);

  const generated = analytics?.generated_at
    ? new Date(analytics.generated_at).toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className={s.page}>
      <PlatformPageHeader
        title="Сводка"
        lead={
          generated
            ? `Живые метрики платформы · обновлено ${generated}`
            : "Живые метрики платформы"
        }
      />

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка аналитики…</div> : null}

      {!loading && analytics ? (
        <>
          <section className={s.kpiStrip}>
            <MetricCard
              label="MRR"
              value={`${formatRub(overview.mrr_estimate_rub)} ₽`}
              hint={`ARR ${formatRub(overview.arr_estimate_rub)} ₽ · 30д ${formatRub(forecast.mrr_next_30d_rub)} ₽`}
              tip={METRIC_TIPS.mrr}
              accent
              trend={forecast?.tenants?.trend}
              to="/admin/payments"
            />
            <MetricCard
              label="Клубы"
              value={overview.tenants_total || 0}
              hint={`Активных ${overview.tenants_by_status?.active || 0} · trial ${overview.tenants_by_status?.trial || 0} · +${growth.new_tenants_30d || 0} за 30д`}
              tip={METRIC_TIPS.clubs}
              trend={
                (growth.growth_rate_pct || 0) > 5
                  ? "up"
                  : (growth.growth_rate_pct || 0) < -5
                    ? "down"
                    : "flat"
              }
              to="/admin/tenants"
            />
            <MetricCard
              label="В оплату"
              value={pctLabel(funnel?.conversion?.overall_to_paid)}
              hint={`Удержание ${pctLabel(funnel?.conversion?.retention_rate)} · trial→pay ${pctLabel(funnel?.conversion?.trial_to_paid ?? forecast.trial_to_paid_rate)}`}
              tip={METRIC_TIPS.saas_funnel}
              to="/admin/funnel"
            />
            <MetricCard
              label="В работе"
              value={activity.active_clubs_30d || 0}
              hint={`Утилизация ${activity.utilization_pct || 0}% · здоровье ${overview.health_score || 0}`}
              tip={METRIC_TIPS.health}
            />
            <MetricCard
              label="Риск"
              value={(funnel?.at_risk_count || 0) + (atRisk.expiring_soon || 0)}
              hint={`Истекают ${atRisk.expiring_soon || 0} · потеряны ${funnel?.lost_count || 0}`}
              tip={METRIC_TIPS.churn_risk}
              warn={(funnel?.at_risk_count || 0) + (atRisk.expiring_soon || 0) > 0}
              to="/admin/tenants?filter=outreach"
            />
          </section>

          <section className={s.biGrid}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Динамика</h3>
                <div className={s.chartTabs} role="tablist">
                  {CHART_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={chartTab === tab.id}
                      className={
                        chartTab === tab.id
                          ? `${s.chartTab} ${s.chartTabOn}`
                          : s.chartTab
                      }
                      onClick={() => setChartTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {chartTab === "revenue" ? (
                revenueSeries.some((p) => p.amount > 0) ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={revenueSeries}>
                      <defs>
                        <linearGradient id="biRev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0f766e" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(15,23,42,0.06)"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        width={52}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        content={
                          <ChartTooltipBox
                            valueFormatter={(v) => `${formatRub(v)} ₽`}
                          />
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        name="Выручка"
                        stroke="#0f766e"
                        fill="url(#biRev)"
                        strokeWidth={2.5}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className={s.emptyHint}>Пока нет подтверждённых оплат</p>
                )
              ) : null}

              {chartTab === "signups" ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={signupSeries}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(15,23,42,0.06)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      width={28}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltipBox />} />
                    <Bar
                      dataKey="actual"
                      name="Факт"
                      fill="#2563eb"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={26}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecast"
                      name="Прогноз"
                      stroke="#0f766e"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : null}

              {chartTab === "sessions" ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={sessionSeries}>
                    <defs>
                      <linearGradient id="biSes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(15,23,42,0.06)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      width={32}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltipBox />} />
                    <Area
                      type="monotone"
                      dataKey="actual"
                      name="Факт"
                      stroke="#2563eb"
                      fill="url(#biSes)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecast"
                      name="Прогноз"
                      stroke="#ca8a04"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : null}
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Очередь действий</h3>
                <Link to="/admin/tenants?filter=outreach" className={s.panelLink}>
                  Прозвон
                </Link>
              </div>
              <ActionFeed items={actionItems} />
            </div>
          </section>

          <section className={s.biBottom}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Воронка</h3>
                <Link to="/admin/funnel" className={s.panelLink}>
                  Открыть
                </Link>
              </div>
              <MiniFunnel funnel={funnel} />
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Тарифы</h3>
                <Link to="/admin/plans" className={s.panelLink}>
                  Управление
                </Link>
              </div>
              <div className={s.planMix}>
                {(subscriptions.by_plan || []).map((p) => (
                  <div key={p.bucket} className={s.planMixRow}>
                    <span>{p.label}</span>
                    <strong>{p.count}</strong>
                    <small>
                      {p.mrr_rub ? `${formatRub(p.mrr_rub)} ₽` : "без MRR"}
                    </small>
                  </div>
                ))}
              </div>
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Последние оплаты</h3>
                <Link to="/admin/payments" className={s.panelLink}>
                  Все
                </Link>
              </div>
              {recentPayments.length ? (
                <div className={s.payList}>
                  {recentPayments.map((p) => (
                    <Link
                      key={p.id}
                      to={
                        p.tenant_id
                          ? `/admin/tenants/${p.tenant_id}`
                          : "/admin/payments"
                      }
                      className={s.payRow}
                    >
                      <div className={s.payMeta}>
                        <strong>{p.tenant_name || p.tenant_subdomain || "Клуб"}</strong>
                        <small>
                          {p.plan_name || "Тариф"} ·{" "}
                          {p.confirmed_at
                            ? new Date(p.confirmed_at).toLocaleDateString("ru-RU")
                            : "—"}
                        </small>
                      </div>
                      <span className={s.payAmount}>{formatRub(p.amount)} ₽</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className={s.emptyHint}>Подтверждённых оплат пока нет</p>
              )}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
